

import { Component, Vue, Prop, Inject } from "vue-property-decorator"

import vSelect from "vue-select"

import UserImage from "@/components/UserImage.vue"

import { CaseDetails, CaseAudit, Field } from "@/types"
import { TimeEntry } from "@/types/timeEntry"
import { caseColumns } from "@/types/caseColumns"
import { FieldTypeId } from "@/types/fieldTypes"

import CaseAuditHistoryService from "@/services/caseAuditHistoryService"
import { formatDateMixin } from "@/mixins/formatDateMixin"

@Component({
	components: {
		"user-image": UserImage,
		"v-select": vSelect
	}
})
export default class History extends Vue {
	@Inject()
	caseAuditHistoryService!: CaseAuditHistoryService

	@Prop()
	public currentCase!: CaseDetails | null

	stringMatchesFriendlyDateAndTimeFormat = ((this as any).stringMatchesFriendlyDateAndTimeFormat)

	private get auditHistory (): CaseAudit[] {
		const auditHistory = this.caseAuditHistoryService?.auditHistory ?? []

		// Ignore audits where a text field went from null to an empty string.
		const filteredAudits = auditHistory.filter(audit => !(audit.oldValue === null && audit.newValue === ""))

		return filteredAudits
	}

	private getField (fieldId: string): Field | null {
		const caseColumn = caseColumns.firstOrDefault(c => c.key === fieldId)
		if (caseColumn) {
			return {
				id: caseColumn.key,
				title: caseColumn.label,
				dataType: caseColumn.dataType
			} as Field
		}

		const caseField = this.currentCase?.fields[fieldId]

		if (caseField) {
			return caseField.field
		}

		return null
	}

	private getFormattedAuditValue (audit: CaseAudit, newValue: boolean): string | null {
		const value = newValue ? audit.newValue : audit.oldValue
		const valueLabel = newValue ? audit.newValueLabel : audit.oldValueLabel

		let valueToDisplay = valueLabel ?? value ?? null

		if (!audit.fieldId) return valueToDisplay

		const field = this.getField(audit.fieldId)
		if (!field) return valueToDisplay

		if (field.dataType === FieldTypeId.Date || field.dataType === FieldTypeId.DateTime) {
			// output UTC date audits in the user's local time zone
			valueToDisplay = formatDateMixin.methods.displayDateFromISOString(value)
		} else if (field.dataType === FieldTypeId.UserRole && typeof valueToDisplay === "string" && valueToDisplay.trim()) {
			let rawEntry: any

			try {
				rawEntry = JSON.parse(valueToDisplay)
			} catch {
				return valueToDisplay
			}

			const isTimeEntryAudit = rawEntry &&
				typeof rawEntry.Id === "string" &&
				typeof rawEntry.Date === "string" &&
				(Number.isFinite(rawEntry.Time) || !isNaN(parseFloat(rawEntry.Time)))

			if (!isTimeEntryAudit) return valueToDisplay // Use valueToDisplay for other user-role related audits

			const entry: TimeEntry = {
				id: rawEntry.Id,
				date: new Date(rawEntry.Date),
				time: Number(rawEntry.Time)
			}

			const formattedDate = `Date: ${formatDateMixin.methods.displayUtcDateOnlyFromJsDate(entry.date)}`
			const formattedTime = `Time: ${entry.time}`

			valueToDisplay = `(${formattedDate}, ${formattedTime})`
		}

		return valueToDisplay
	}
}
